import type { ReportAnswers, ReportAnswerValue } from '@dte/otw-azure-functions/src/report/CustomerReport';
import {
	getAnswerValue,
	getAnswerValues,
	getFilteredReportAnswers,
	getSelectedAnswerValues,
	isAnswerValueSelected,
	setAnswerValue,
} from '@dte/otw-azure-functions/src/report/getQuestion';
import { ReportQuestion } from '@dte/otw-azure-functions/src/report/questions/ReportQuestion';
import { addValue } from '@dte/otw-utils-core/src/lists/addValue';
import { filterBlank } from '@dte/otw-utils-core/src/lists/filterEmpty';
import { removeValue } from '@dte/otw-utils-core/src/lists/removeValue';
import { copyJson } from '@dte/otw-utils-core/src/objects/copyJson';
import { isNotBlankValue } from '@dte/otw-utils-core/src/objects/isBlankValue';
import { isEmptyArray } from '@dte/otw-utils-core/src/objects/isEmpty';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useJsonEqualValue } from '../../../../../../hooks/useJsonEqualState';
import { useSetAtomWithType } from '../../../../../../hooks/useSetAtomWithType';
import { ReportStep } from '../../ReportSteps';
import { workingDraftReportWithCurrentStepAtom } from '../../workingDraftReportWithCurrentStepAtom';
import { workingDraftReportAtom } from './draftReportAtoms';
import { currentStepAtom } from './useCurrentStep';

export const answersAtom = atom(
	(get) => {
		const draftReport = get(workingDraftReportAtom);
		return draftReport?.answers ?? {};
	},
	(get, set, newAnswers: ReportAnswers) => {
		const draftReport = get(workingDraftReportAtom);
		const newDraftReport = copyJson(draftReport);
		newDraftReport.answers = copyJson(newAnswers);
		set(workingDraftReportAtom, newDraftReport);
	},
);
answersAtom.debugLabel = 'answers';

// Answers that are selected
export const selectedAnswersAtom = atom((get) => {
	const draftReport = get(workingDraftReportAtom);
	return getFilteredReportAnswers(draftReport);
});
selectedAnswersAtom.debugLabel = 'selectedAnswers';

export function useReportAnswerValue(questionName: ReportQuestion, answerKey?: string): string {
	const reportAnswerAtom = useMemo(() => {
		const atomInstance = atom((get) => {
			const answers = get(answersAtom);
			const answerValue = getAnswerValue(answers, questionName, answerKey);
			return answerValue?.value ?? '';
		});
		atomInstance.debugLabel = `answerValue(${questionName}, ${answerKey})`;
		return atomInstance;
	}, [questionName, answerKey]);

	return useAtomValue(reportAnswerAtom);
}

export function useSelectedReportAnswers(questionName: ReportQuestion, answerKey?: string): string[] {
	const reportAnswersAtom = useMemo(() => {
		const atomInstance = atom((get) => {
			const answers = get(answersAtom);
			const questionAnswers = getSelectedAnswerValues(answers, questionName);

			const keys = Object.keys(questionAnswers);
			if (isEmptyArray(keys)) {
				return [];
			}
			return keys;
		});
		atomInstance.debugLabel = `selectedReportAnswers(${questionName}, ${answerKey})`;
		return atomInstance;
	}, [questionName, answerKey]);

	return useAtomValue(reportAnswersAtom);
}

export function useSelectedReportAnswer(questionName: ReportQuestion, answerKey?: string): string | undefined {
	const answers = useSelectedReportAnswers(questionName, answerKey);

	if (isEmptyArray(answers)) {
		return undefined;
	}

	return answers[0];
}

export function updateCategories(
	questionName: ReportQuestion,
	answerValue: ReportAnswerValue,
	selected?: boolean,
	currentStep?: ReportStep,
) {
	// For Observations, tie them to a question that we know is always answered
	if (ReportQuestion.Observations === questionName) {
		answerValue.categories = [ReportStep.Reporter];
	} else {
		if (selected) {
			answerValue.categories = addValue(answerValue.categories, currentStep);
		} else {
			answerValue.categories = removeValue(answerValue.categories, currentStep);
		}
	}
}

export function useUpdateAnswerCategory(
	questionName: ReportQuestion,
	answerKey?: string,
): (selectCategory: boolean) => void {
	const addCategoryAtom = useMemo(() => {
		return atom(null, (get, set, selectCategory: boolean) => {
			const answers = get(answersAtom);
			const newAnswers = copyJson(answers);
			const answerValue = getAnswerValue(newAnswers, questionName, answerKey);

			// Update the categories for this answer value
			updateCategories(questionName, answerValue, selectCategory, get(currentStepAtom));

			setAnswerValue(newAnswers, questionName, answerKey, answerValue);
			set(answersAtom, newAnswers);
		});
	}, [questionName, answerKey]);

	const [, setAddCategory] = useAtom(addCategoryAtom);

	useEffect(() => {
		// Add the category when the component mounts
		setTimeout(() => {
			setAddCategory(true);
		}, 0);

		return () => {
			// Remove the category when the component unmounts
			setTimeout(() => {
				setAddCategory(false);
			}, 0);
		};
	}, [setAddCategory]);

	return setAddCategory;
}

export function useSetReportAnswerValue(questionName: ReportQuestion, answerKey?: string): (value: string) => void {
	useUpdateAnswerCategory(questionName, answerKey);

	const reportAnswerAtom = useMemo(() => {
		const atomInstance = atom(null, (get, set, newValue: string) => {
			const answers = get(answersAtom);
			const newAnswers = copyJson(answers);

			const answerValue = getAnswerValue(newAnswers, questionName, answerKey);

			if (isNotBlankValue(newValue)) {
				answerValue.value = newValue;

				// If we're modifying the value, make sure we have a category set
				const currentStep = get(currentStepAtom);
				updateCategories(questionName, answerValue, true, currentStep);
			} else {
				answerValue.value = undefined;
			}

			answerValue.custom = true;
			answerValue.selected = isNotBlankValue(newValue);

			setAnswerValue(answers, questionName, answerKey, answerValue);

			set(answersAtom, newAnswers);
		});
		atomInstance.debugLabel = `answerValue(${questionName}, ${answerKey})`;
		return atomInstance;
	}, [questionName, answerKey]);

	return useSetAtomWithType(reportAnswerAtom);
}

export function useSetReportAnswerSelected(
	question: ReportQuestion,
	answerKey?: string,
	mutuallyExclusiveAnswers?: string[],
): (selected: boolean) => void {
	const mutuallyExclusiveAnswersValue = useJsonEqualValue(mutuallyExclusiveAnswers);

	useUpdateAnswerCategory(question, answerKey);

	const answerSelectedAtom = useMemo(() => {
		// Other answers that may need to be unselected if this is selected
		let otherAnswerKeys = filterBlank(mutuallyExclusiveAnswersValue);
		otherAnswerKeys = otherAnswerKeys.filter((k) => k !== answerKey);

		return atom(null, (get, set, newSelected: boolean) => {
			const answers = get(answersAtom);
			const newAnswers = copyJson(answers);

			const answerValue = getAnswerValue(newAnswers, question, answerKey);
			answerValue.selected = newSelected;
			setAnswerValue(newAnswers, question, answerKey, answerValue);

			if (newSelected) {
				for (const otherAnswerKey of otherAnswerKeys) {
					const otherAnswerValue = getAnswerValue(newAnswers, question, otherAnswerKey);
					otherAnswerValue.selected = false;
					setAnswerValue(newAnswers, question, otherAnswerKey, otherAnswerValue);
				}
			}

			set(answersAtom, newAnswers);
		});
	}, [question, answerKey, mutuallyExclusiveAnswersValue]);

	return useSetAtomWithType(answerSelectedAtom);
}

export function useReportAnswerValueSelected(question: ReportQuestion, answerKey?: string): boolean {
	const answerSelectedAtom = useMemo(() => {
		return atom((get) => {
			// Filter to only questions that have been answered as part of the current flow
			const report = get(workingDraftReportWithCurrentStepAtom);
			const answers = getFilteredReportAnswers(report);

			return isAnswerValueSelected(answers, question, answerKey);
		});
	}, [question, answerKey]);

	return useAtomValue(answerSelectedAtom);
}

export function useSelectReportAnswerValue(question: ReportQuestion): (newSelectedValue: string) => void {
	// const selectedValue = useReportAnswerValue(question);

	// TODO: figure out how to do this in a more generic way
	// useUpdateAnswerCategory(question, selectedValue);

	const answerSelectedAtom = useMemo(() => {
		return atom(null, (get, set, newSelectedValue: string) => {
			const answers = get(answersAtom);
			const newAnswers = copyJson(answers);

			// Select the new answer
			const answerValue = getAnswerValue(newAnswers, question, newSelectedValue);
			answerValue.selected = true;
			setAnswerValue(newAnswers, question, newSelectedValue, answerValue);

			// Unselect other answers
			const answerValues = getAnswerValues(answers, question);
			let otherAnswerKeys = Object.keys(answerValues);
			otherAnswerKeys = otherAnswerKeys.filter((key) => key !== newSelectedValue);

			for (const otherAnswerKey of otherAnswerKeys) {
				const otherAnswerValue = getAnswerValue(newAnswers, question, otherAnswerKey);
				otherAnswerValue.selected = false;
				setAnswerValue(newAnswers, question, otherAnswerKey, otherAnswerValue);
			}

			set(answersAtom, newAnswers);
		});
	}, [question]);

	return useSetAtomWithType(answerSelectedAtom);
}
